import { dayjs } from '@koob/dates';

// The pieces of a program day's own pricing request and extras bookkeeping
// that used to sit inline in the day component (US-064). Pure: the hook that
// mounts them owns every effect.

type Composition =
  | { adults?: number; childrenBirthdates?: unknown[] }
  | null
  | undefined;

interface RequestFiltersInput {
  experience: any;
  date: unknown;
  composition: Composition;
  templateId?: string | null;
  // The app's own fields for the request (`getExperiencePricingFilters`).
  extraFilters?: Record<string, unknown>;
}

// `experience.dayIndex` is 1-based inside a multi-day program: the request
// always covers the whole instance, so it is anchored on the program's first
// day whichever day of it is being drawn.
export function getPricingRequestFilters({
  experience,
  date,
  composition,
  templateId,
  extraFilters,
}: RequestFiltersInput) {
  const experienceDate = dayjs(date as any);
  const hasValidDate = experienceDate.isValid();
  // KD-3906: a day without a usable position (no dayIndex, or no
  // durationDays) prices on its own date instead of an invalid one.
  const dayIndex = Number(experience?.dayIndex);
  const durationDays = Number(experience?.durationDays ?? 1);
  const hasValidPosition =
    Number.isFinite(dayIndex) &&
    dayIndex > 0 &&
    Number.isFinite(durationDays) &&
    durationDays > 0;

  let startAt: string | undefined;
  let endAt: string | undefined;
  if (hasValidDate) {
    startAt = hasValidPosition
      ? experienceDate.subtract(dayIndex - 1, 'days').toISOString()
      : experienceDate.toISOString();
    endAt = hasValidPosition
      ? experienceDate.subtract(dayIndex - durationDays, 'days').toISOString()
      : experienceDate.toISOString();
  }

  return {
    hasValidDate,
    filters: {
      experiences: [experience?.id],
      experienceComposition: composition,
      startAt,
      endAt,
      templateId: templateId ?? undefined,
      isForTripSearch: true,
      ...extraFilters,
    },
  };
}

// The filters an experience is stamped with the first time it is drawn, so a
// later screen can reopen its pricing on the same party and dates.
export function getInitialExperienceFilters({
  experience,
  date,
  composition,
  templateId,
}: RequestFiltersInput) {
  const experienceDate = dayjs(date as any);

  return {
    experiences: [experience.id],
    experienceComposition: composition,
    startAt: experienceDate.toISOString(),
    endAt: experienceDate
      .add(experience.durationDays ?? 1, 'day')
      .toISOString(),
    templateId: templateId ?? undefined,
    isForTripSearch: true,
  };
}

// Ids of the extras a program already includes with its accommodation: they
// are not offered as options.
export function getAccommodationExtraIds(experience: any): string[] {
  return [
    ...new Set<string>(
      experience?.programs?.reduce(
        (acc: string[], program: any) =>
          acc.concat(program?.includedExperiences?.map((item: any) => item.id)),
        [],
      ),
    ),
  ];
}

// The extras selected across every day of this program instance.
// `experienceStartIndex` is the array position of the instance's first day.
export function getInstanceExtras(
  days: any[],
  experience: any,
  experienceStartIndex: number,
): any[] {
  return days.reduce((acc: any[], day: any, arrayIndex: number) => {
    if (
      day?.experienceId === experience?.id &&
      arrayIndex >= experienceStartIndex &&
      arrayIndex < experienceStartIndex + (experience?.durationDays ?? 1)
    ) {
      const dayExtras = Array.isArray(day.selectedExtras)
        ? day.selectedExtras
        : [];
      acc.push(
        ...dayExtras.filter(
          (extra: any) =>
            extra.experienceId === experience.id ||
            extra.parentExtraId === experience.id,
        ),
      );
    }
    return acc;
  }, []);
}
